/**
 * Display-scale numbers, shared by the evidence page (apps/web) and the
 * Worker's alert messages (F19 ALRT-034), so an alert prints the number the
 * reader then sees on the page. Locale-free, with the Unicode minus sign.
 *
 * Implements PAGE-150, PAGE-151, PAGE-307 and ALRT-034.
 */
import { measureInfo, type Measure } from '@bioterafi/stats';

export const MINUS = '−';

export function withMinus(text: string): string {
  return text.replace(/^-/, MINUS);
}

/** Two decimals below 10, one from 10, none from 100, two significant figures below 0.01 (PAGE-307). */
function formatMagnitude(value: number): string {
  const a = Math.abs(value);
  if (a === 0) return '0.00';
  if (a >= 100) return withMinus(value.toFixed(0));
  if (a >= 10) return withMinus(value.toFixed(1));
  if (a >= 0.01) return withMinus(value.toFixed(2));
  return withMinus(value.toPrecision(2));
}

/** A proportion as a percentage: one decimal, two significant figures below 0.1% (PAGE-307). */
function formatProportion(value: number): string {
  const pct = value * 100;
  if (pct !== 0 && Math.abs(pct) < 0.1) return `${withMinus(pct.toPrecision(2))}%`;
  return `${withMinus(pct.toFixed(1))}%`;
}

/**
 * A display-scale number (PAGE-150, PAGE-151, PAGE-307): ratios to two decimals (more when tiny),
 * risk differences to three, SMD and r to two, mean differences and rates by magnitude, and
 * proportions as percentages.
 */
export function formatDisplayValue(measure: Measure, displayValue: number): string {
  if (!Number.isFinite(displayValue)) return displayValue > 0 ? '∞' : `${MINUS}∞`;
  switch (measureInfo(measure).family) {
    case 'proportion':
      return formatProportion(displayValue);
    case 'correlation':
      return withMinus(displayValue.toFixed(2));
    case 'rate':
      return formatMagnitude(displayValue);
    default:
      break;
  }
  if (measure === 'RD') {
    const v = Math.abs(displayValue) < 0.0005 ? 0 : displayValue;
    return withMinus(v.toFixed(3));
  }
  if (measure === 'SMD') return withMinus((Math.abs(displayValue) < 0.005 ? 0 : displayValue).toFixed(2));
  if (measure === 'MD' || measure === 'GEN') return formatMagnitude(displayValue);
  const a = Math.abs(displayValue);
  if (a >= 100) return displayValue.toFixed(0);
  if (a >= 10) return displayValue.toFixed(1);
  if (a >= 0.095) return displayValue.toFixed(2);
  return withMinus(displayValue.toPrecision(2));
}

/** The measure's name in running text, e.g. "risk ratio" (the engine's registry name, lower case). */
export function measureNameInText(measure: Measure): string {
  return measureInfo(measure).name.toLowerCase();
}
